import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { Box, Paper, Grid, IconButton, Button, Tabs, Tab, TextField, Autocomplete, Typography, Chip } from "@mui/material";
import { useState, useEffect } from "react";
import { AddSupplementaryRequestVendorEmailsDto, SupplementaryRequestVendorEmail } from "../helpers/InterfacesServer";
import { VerticalScrollableComponent } from "../layouts/VerticalScrollableComponent";
import { primaryColor } from "../layouts/theme";
import { addSupplementaryRequestVendorEmails, getAllSupplementaryRequestVendorEmails, getSupplementaryRequestVendorEmailCompanyLetter } from "../api-services/VendorOnboardingApi";
import { Download, Upload } from "@mui/icons-material";
import { CustomTabPanel } from '../layouts/CustomTabPanel';
import { showAlertError, showConfirmBox } from '../helpers/Utils';
import { useAppSelector } from '../store/store';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RoutePathNames } from '../helpers/Constants';

export function ViewSupplementaryVendorEmailRequests() {
    //state
    const navigate = useNavigate()
    const [searchParams, ] = useSearchParams();
    const vendors = useAppSelector(state => state.vendors.value);
    const vendorsDict = useAppSelector(state => state.vendorsDict.value);
    const vendorUsersDict = useAppSelector(state => state.vendorUsersDict.value);
    const [supplementaryRequestVendorEmails, setSupplementaryRequestVendorEmails] = useState<SupplementaryRequestVendorEmail[]>([])
    const [currentTab, setCurrentTab] = useState<number>(searchParams.get('AddSupplementaryVendorEmails') == null ? 1 : 2)
    const [selectedVendorCode, setSelectedVendorCode] = useState<string | null | undefined>(searchParams.get('VendorCode'))
    const [emails, setEmails] = useState<string>((searchParams.get('Emails') ?? "").split(',').map(e => e.trim()).filter(e => e !== '').join("\n"))
    const [companyLetterPdfFile, setCompanyLetterPdfFile] = useState<File | null | undefined>(null)


    useEffect(() => {
        getAllSupplementaryRequestVendorEmails().then(supplementaryRequestVendorEmails =>
            setSupplementaryRequestVendorEmails(supplementaryRequestVendorEmails.map(e => ({ ...e, Status: vendorUsersDict.has(e.Email) ? vendorUsersDict.get(e.Email)!.InvitationStatus : 'Not Invited' })))
        )
    }, [vendorUsersDict])


    function handleAddSupplementaryRequestVendorEmails() {
        const emailList = emails.split("\n").map(email => email.trim()).filter(email => email !== '')
        // check that each email is valid
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const invalidEmails = emailList.filter(email => !emailRegex.test(email));
        if (invalidEmails.length > 0) {
            showAlertError(`Invalid email addresses:\n${invalidEmails.join('\n')}`);
            return;
        }
        // proceed to create dto and call api
        const dto: AddSupplementaryRequestVendorEmailsDto = {
            VendorCode: selectedVendorCode!,
            Emails: emailList,
            CompanyLetterPdf: companyLetterPdfFile!
        }
        addSupplementaryRequestVendorEmails(dto).then(result => {
            setSupplementaryRequestVendorEmails(result)
            // reset form
            setSelectedVendorCode(null);
            setEmails("");
            setCompanyLetterPdfFile(null);
            handleConfirmNoAdditionalEmailsToOnboard();
        })
    }

    function handleConfirmNoAdditionalEmailsToOnboard() {
        showConfirmBox("Do you have additional supplementary Emails to Add? If No, We will proceed to onboard the vendor emails on TotalEnergies.", "Additonal Supplementary Emails to Onboard", "Yes", "No - Proceed to Onboard")
            .then((confirmResult) => {
                if (!confirmResult.isConfirmed) {
                    navigate("/" + RoutePathNames.ONBOARD_PENDING_VENDORS + '?initiateOnboardingOnLoad=1')
                }
            })
    }


    function handleUploadCompanyLetterPDF(event: React.ChangeEvent<HTMLInputElement>) {
        event.persist(); //important to persist event so we can reset the value of the input file after an upload
        if (event.target.files == null || event.target.files.length === 0) return;
        setCompanyLetterPdfFile(event.target.files[0]);
        event.target.value = '' //important to persist event so we can reset the value of the input file after an upload
    }


    // Define columns
    const columns: MRT_ColumnDef<SupplementaryRequestVendorEmail>[] = [
        { accessorKey: 'Email', header: 'Email', grow: 1 },
        { accessorKey: 'VendorCode', header: 'Vendor Code', size: 150 },
        { accessorFn: (row) => vendorsDict.get(row.VendorCode)?.VendorName, header: 'Vendor', size: 250 },
        {
            accessorKey: 'CompanyLetterPdfFileName',
            header: 'Company Letter',
            Cell: ({ cell }) => {
                return (
                    <IconButton color="info" edge="end" onClick={() => { getSupplementaryRequestVendorEmailCompanyLetter(cell.getValue<string>()) }} title='Download Request Letter' >
                        <Download />
                    </IconButton>
                );
            },
        },
        { accessorKey: 'CreatedDate', header: 'Created Date', Cell: ({ cell }) => new Date(cell.getValue<Date>()).toLocaleString(), },
        { accessorKey: 'CreatedBy', header: 'Created By', grow: 1 },
        {
            accessorKey: 'Status',
            header: 'Onboarding Status',
            size: 200,
            Cell: ({ cell }) => (
                <Chip
                    label={cell.getValue<string>()}
                    color={cell.getValue<string>() === 'Accepted' ? 'success' : (cell.getValue<string>() === 'PendingAcceptance' ? 'warning' : 'error')}
                    size="small"
                />
            ),
        },
    ];



    return (
        <Box className='HorizontalOverFlowContainer'>
            <Paper className="Form">
                <br />
                {vendors.length > 0 && supplementaryRequestVendorEmails.length > 0 &&
                    <Box sx={{ width: '100%' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={currentTab} onChange={(event, newValue) => setCurrentTab(Number(newValue))} textColor="secondary" indicatorColor="secondary">
                                <Tab label="View Supplementary Vendor Emails" value={1} />
                                <Tab label="Add Supplementary Vendor Email" value={2} />
                            </Tabs>
                        </Box>
                        <CustomTabPanel tabValue={1} selectedTab={currentTab}>
                            <VerticalScrollableComponent viewPortHeightOffset={230}>
                                <Grid style={{ padding: 10 }} >
                                    <MaterialReactTable<SupplementaryRequestVendorEmail> columns={columns} data={supplementaryRequestVendorEmails} enableColumnResizing enablePagination enableSorting enableColumnFilters
                                        mrtTheme={{ baseBackgroundColor: "#ffffff", }} muiTablePaperProps={{ elevation: 0, }}
                                    />
                                </Grid>
                            </VerticalScrollableComponent>
                        </CustomTabPanel>
                        <CustomTabPanel tabValue={2} selectedTab={currentTab}>
                            <VerticalScrollableComponent viewPortHeightOffset={230}>
                                <Grid style={{ padding: 10 }} >
                                    <Box component="form" sx={{ mt: 2 }}>
                                        <Grid container spacing={3}>
                                            <Grid size={{ xs: 12, lg: 6 }}>
                                                <Typography sx={{ display: 'inline', color: primaryColor }}>Vendor Code:</Typography>
                                                <Autocomplete size="small" clearOnEscape id="otherEntityResponsible" options={vendors.filter(e => e.IsActive)} getOptionLabel={(row) => row.VendorCode + " - " + row.VendorName}
                                                    value={vendors.find(v => v.VendorCode === selectedVendorCode)} isOptionEqualToValue={(option, value) => option.VendorCode === value.VendorCode}
                                                    onChange={(event, value) => setSelectedVendorCode(value?.VendorCode)} renderInput={(params) => (<TextField {...params} variant="outlined" color="primary" />)}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <Typography sx={{ display: 'inline', color: primaryColor }}> Supplementary Email Addresses:</Typography>
                                                <TextField fullWidth placeholder="Enter email addresses on each line" multiline rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} />
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <Button variant="contained" size="small" color="info" component="label"><Upload /> &nbsp;Upload Company Letter PDF
                                                    <input accept="application/pdf" hidden type="file" onChange={(event) => handleUploadCompanyLetterPDF(event)} />
                                                </Button>
                                                <Typography variant="body2" sx={{ fontStyle: 'italic', display: 'inline', marginLeft: 2, color: '#555555' }}>
                                                    {companyLetterPdfFile ? companyLetterPdfFile.name : 'No file selected'}
                                                </Typography>
                                            </Grid>
                                            <Grid container justifyContent="right" size={{ xs: 12 }}>
                                                <Button sx={{ width: 200 }} disabled={!selectedVendorCode || emails.trim() === "" || !companyLetterPdfFile} variant="contained" color="secondary" onClick={() => { handleAddSupplementaryRequestVendorEmails() }}>Submit</Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            </VerticalScrollableComponent>
                        </CustomTabPanel>
                    </Box>
                }
                <br />
            </Paper>
        </Box>
    )
}
